import React from "react";
import { useTranslation } from "react-i18next";

interface VehicleFormData {
  year: string;
  maker: string;
  model: string;
  name: string;
  description: string;
}

interface VehicleFormErrors {
  year?: string;
  maker?: string;
  model?: string;
  name?: string;
  description?: string;
}

interface VehicleFormProps {
  vehicleData: VehicleFormData;
  errors: VehicleFormErrors;
  onUpdateField: (field: keyof VehicleFormData, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  isEditing: boolean;
}

export const VehicleForm: React.FC<VehicleFormProps> = ({
  vehicleData,
  errors,
  onUpdateField,
  onSubmit,
  onCancel,
  isSubmitting,
  isEditing,
}) => {
  const { t } = useTranslation(["pages", "common"]);
  return (
 <form onSubmit={onSubmit} className="space-y-6">
 <div className="grid grid-cols-1 gap-6">
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label
              htmlFor="year"
              className="block text-sm font-medium text-secondary-700 mb-2"
            >
              {t("pages:vehicles.form.year")} *
            </label>
            <input
              type="number"
              id="year"
              value={vehicleData.year}
              onChange={(e) => onUpdateField("year", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.year ? "border-red-500" : "border-secondary-300"
              }`}
              placeholder={t("pages:vehicles.form.yearPlaceholder")}
              min={1900}
              max={new Date().getFullYear() + 5}
              required
            />
            {errors.year && (
              <p className="mt-1 text-sm text-red-600">{errors.year}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="maker"
              className="block text-sm font-medium text-secondary-700 mb-2"
            >
              {t("pages:vehicles.form.maker")} *
            </label>
            <input
              type="text"
              id="maker"
              value={vehicleData.maker}
              onChange={(e) => onUpdateField("maker", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.maker ? "border-red-500" : "border-secondary-300"
              }`}
              placeholder={t("pages:vehicles.form.makerPlaceholder")}
              required
            />
            {errors.maker && (
              <p className="mt-1 text-sm text-red-600">{errors.maker}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="model"
              className="block text-sm font-medium text-secondary-700 mb-2"
            >
              {t("pages:vehicles.form.model")} *
            </label>
            <input
              type="text"
              id="model"
              value={vehicleData.model}
              onChange={(e) => onUpdateField("model", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.model ? "border-red-500" : "border-secondary-300"
              }`}
              placeholder={t("pages:vehicles.form.modelPlaceholder")}
              required
            />
            {errors.model && (
              <p className="mt-1 text-sm text-red-600">{errors.model}</p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("pages:vehicles.form.name")}
          </label>
          <input
            type="text"
            id="name"
            value={vehicleData.name}
            onChange={(e) => onUpdateField("name", e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.name ? "border-red-500" : "border-secondary-300"
            }`}
            placeholder={t("pages:vehicles.form.namePlaceholder")}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
          <p className="mt-1 text-sm text-secondary-600">
            {t("pages:vehicles.form.nameHelp")}
          </p>
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("common:description")}
          </label>
          <textarea
            id="description"
            value={vehicleData.description}
            onChange={(e) => onUpdateField("description", e.target.value)}
            rows={4}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              errors.description ? "border-red-500" : "border-secondary-300"
            }`}
            placeholder={t("pages:vehicles.form.descriptionPlaceholder")}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
          <p className="mt-1 text-sm text-secondary-600">
            {t("pages:vehicles.form.descriptionHelp")}
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-4 pt-6 border-t border-secondary-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          disabled={isSubmitting}
        >
          {t("common:cancel")}
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-4 w-4 text-white inline"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {isEditing
                ? t("common:updating")
                : t("common:creating")}
            </>
          ) : (
            <>
              {isEditing
                ? t("pages:vehicles.form.update")
                : t("pages:vehicles.form.create")}
            </>
          )}
        </button>
      </div>
    </form>
  );
};
